/**
 * Horizon Pro — loads Material Symbols with only the icons present on the page.
 * Icons are rendered as <span class="pro-icon" data-icon="name">name</span> (snippets/pro-icon.liquid).
 */
(() => {
  const FONT_FAMILY = 'Material Symbols Outlined';
  const BASE_URL = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,300,0..1,0';
  const VALID_NAME = /^[a-z0-9_]+$/;
  const READY_CLASS = 'pro-icons-ready';
  const READY_FALLBACK_MS = 3000;

  /** @type {HTMLLinkElement | null} */
  let link = null;
  let requestedKey = '';

  function collectIconNames() {
    const names = new Set();
    for (const element of document.querySelectorAll('.pro-icon[data-icon]')) {
      const name = (element.getAttribute('data-icon') || '').trim().toLowerCase();
      if (VALID_NAME.test(name)) names.add(name);
    }
    return [...names].sort();
  }

  function markReady() {
    document.documentElement.classList.add(READY_CLASS);
  }

  function load() {
    const names = collectIconNames();
    if (names.length === 0) {
      markReady();
      return;
    }

    const key = names.join(',');
    if (key === requestedKey) return;
    requestedKey = key;

    const nextLink = document.createElement('link');
    nextLink.rel = 'stylesheet';
    nextLink.href = `${BASE_URL}&icon_names=${key}&display=block`;
    nextLink.addEventListener('load', () => {
      link?.remove();
      link = nextLink;
      document.fonts
        .load(`24px "${FONT_FAMILY}"`, names[0])
        .then(markReady, markReady);
    });
    nextLink.addEventListener('error', markReady);
    document.head.appendChild(nextLink);
    setTimeout(markReady, READY_FALLBACK_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', load, { once: true });
  } else {
    load();
  }

  if (window.Shopify?.designMode) {
    for (const eventName of ['shopify:section:load', 'shopify:block:select']) {
      document.addEventListener(eventName, () => requestAnimationFrame(load));
    }
  }

  window.ProIcons = { refresh: load };
})();

/**
 * Muted autoplay videos (<video data-pro-autoplay preload="none">) load and play only while on screen.
 */
(() => {
  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        const video = /** @type {HTMLVideoElement} */ (target);
        if (isIntersecting) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    },
    { rootMargin: '200px 0px', threshold: 0.01 }
  );
  const observe = (/** @type {ParentNode} */ root) => root.querySelectorAll('video[data-pro-autoplay]').forEach((video) => observer.observe(video));
  observe(document);
  document.addEventListener('shopify:section:load', (event) => observe(/** @type {any} */ (event.target)));
})();

/* Theme editor: helper notes of Pro blocks are shown only while their block is selected */
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target instanceof Element) event.target.classList.add('pro-block-selected');
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    if (event.target instanceof Element) event.target.classList.remove('pro-block-selected');
  });
}
